import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Portfolio images - real design work examples
const portfolioImages = [
  {
    id: 1,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/pc3b24ls_Comedy%20Show%20Flyer%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  },
  {
    id: 2,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/5wrt2uhf_Sir%20V%20Wood%20-%20Concrete%20Rose%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  },
  {
    id: 3,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/1ns6t8k3_Increase%20Clothing%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  },
  {
    id: 4,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/h8epw2uh_Nano%20Hydration%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  },
  {
    id: 5,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/a94tm9o2_Rare%20Saintz%20-%20Goon%20Angel%20Jacket%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  },
  {
    id: 6,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/cmrdpsan_TrueNorth%20Realty%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  },
  {
    id: 7,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/694ab16o_Foreign%20Glizzy%20-%20Built%20To%20Win%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  },
  {
    id: 8,
    src: 'https://customer-assets.emergentagent.com/job_scenic-frmla/artifacts/2fpprf2t_Girl%20Scoutz%20-%20Vanilla%20Ice%20Cream%20French%20Toast%20%5BPortfolio%20Design%5D.jpg',
    alt: 'Design Work'
  }
];

// Smooth, professional easing
const smoothEase = [0.4, 0, 0.2, 1];

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  const currentImage = images[currentIndex];
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onNext, onPrev]);
  
  // Handle touch events for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) onNext();
    if (isRightSwipe) onPrev();
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: smoothEase }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
    >
      {/* Backdrop - click to close */}
      <div 
        className="absolute inset-0 bg-background/95 backdrop-blur-sm cursor-pointer" 
        onClick={onClose}
      />
      
      {/* Close button */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-6 right-6 z-[110] p-3 text-muted-foreground hover:text-foreground transition-colors duration-300 bg-background/50 rounded-full hover:bg-background/80"
        aria-label="Close lightbox"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>
      
      {/* Navigation - Previous */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 md:left-8 z-[110] p-3 text-muted-foreground hover:text-foreground transition-colors duration-300 bg-background/50 rounded-full hover:bg-background/80"
        aria-label="Previous image"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="w-8 h-8" />
      </button>
      
      {/* Navigation - Next */}
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 md:right-8 z-[110] p-3 text-muted-foreground hover:text-foreground transition-colors duration-300 bg-background/50 rounded-full hover:bg-background/80"
        aria-label="Next image"
        data-testid="lightbox-next"
      >
        <ChevronRight className="w-8 h-8" />
      </button>
      
      {/* Image container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3, ease: smoothEase }}
        className="relative z-[105] w-full h-full flex items-center justify-center p-4 md:p-16 pointer-events-none"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={currentImage.src}
          alt={currentImage.alt}
          className="max-w-full max-h-full object-contain select-none touch-pinch-zoom pointer-events-auto"
          draggable={false}
          style={{ touchAction: 'pinch-zoom' }}
        />
      </motion.div>
      
      {/* Image counter */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[110]">
        <span className="font-body text-xs text-muted-foreground/60 tracking-wider">
          {currentIndex + 1} / {images.length}
        </span>
      </div>
    </motion.div>
  );
};

const SceneSelectedWork = () => {
  const sectionRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);
  
  const openLightbox = useCallback((index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  }, []);
  
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);
  
  const goToNext = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % portfolioImages.length);
  }, []);
  
  const goToPrev = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + portfolioImages.length) % portfolioImages.length);
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="scene relative py-32 overflow-hidden"
      id="selected-work"
    >
      {/* Background - Subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-frmla-dark/50 via-background to-frmla-dark/50" />
      
      <motion.div 
        className="relative z-10 w-full"
        style={{ opacity }}
      >
        {/* Header - Minimal, confident */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: smoothEase }}
          viewport={{ once: true }}
          className="text-center mb-20 px-6"
        >
          <p className="font-body text-sm tracking-[0.3em] text-accent/80 uppercase mb-5">
            PROOF
          </p>
          <div className="gold-line w-16 mx-auto" />
        </motion.div>
        
        {/* Image Grid - Clean, intentional presentation */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
          {portfolioImages.map((image, index) => (
            <motion.button
              key={image.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.7, 
                delay: index * 0.06,
                ease: smoothEase
              }}
              viewport={{ once: true, margin: "-30px" }}
              className="aspect-square overflow-hidden bg-frmla-dark cursor-pointer group"
              onClick={() => openLightbox(index)}
              aria-label={`View image ${index + 1}`}
            >
              <img 
                src={image.src} 
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                draggable={false}
              />
            </motion.button>
          ))}
        </div>
      </motion.div>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={portfolioImages}
            currentIndex={currentImageIndex}
            onClose={closeLightbox}
            onNext={goToNext}
            onPrev={goToPrev}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default SceneSelectedWork;
